import { create } from "apisauce";
import axios from "axios";
import firebase from "firebase/app";
import "firebase/auth";
import Cookies from "js-cookie";
import { getNowDateIso, getNextDateIso } from "../utils/utils";

// baseurlwc apunta al proxy (public/api/wc.php), que agrega las llaves de
// WooCommerce en el servidor. `credentials` queda como parámetro inocuo para
// no tocar las URLs existentes (`?${credentials}&...`).
export const baseurlwc = process.env.REACT_APP_BASE_URL_WC;
export const credentials = "via=proxy";
export const baseurl = process.env.REACT_APP_BASE_URL_API;

const ActualMonth = getNowDateIso();
const NextMonth = getNextDateIso();

const getFirebaseUser = () =>
  new Promise((resolve) => {
    if (!firebase.apps.length) return resolve(null);
    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });

// --- Cola de peticiones a WooCommerce ---------------------------------------
// Imunify360 (WebShield) bloquea IPs con muchas conexiones en pocos segundos.
// Limitamos a MAX_CONCURRENT en paralelo y MIN_GAP_MS entre inicios de petición.
const MAX_CONCURRENT = 2;
const MIN_GAP_MS = 300;
let activeRequests = 0;
let lastStartAt = 0;
const waiting = [];

const pumpQueue = () => {
  while (activeRequests < MAX_CONCURRENT && waiting.length) {
    activeRequests++;
    const start = waiting.shift();
    const delay = Math.max(0, lastStartAt + MIN_GAP_MS - Date.now());
    lastStartAt = Date.now() + delay;
    setTimeout(start, delay);
  }
};
const acquireSlot = () =>
  new Promise((resolve) => {
    waiting.push(resolve);
    pumpQueue();
  });
const releaseSlot = (config) => {
  if (!config || !config.__wcSlot) return;
  config.__wcSlot = false;
  activeRequests = Math.max(0, activeRequests - 1);
  pumpQueue();
};

const withFirebaseToken = (onlyWcUrls) => async (config) => {
  if (onlyWcUrls && !(config.url || "").startsWith(baseurlwc)) return config;
  await acquireSlot();
  config.__wcSlot = true;
  try {
    const user = await getFirebaseUser();
    if (user) {
      const token = await user.getIdToken();
      config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
    }
  } catch (error) {
    releaseSlot(config);
    throw error;
  }
  return config;
};

const releaseOnResponse = (instance) =>
  instance.interceptors.response.use(
    (response) => {
      releaseSlot(response.config);
      return response;
    },
    (error) => {
      releaseSlot(error.config);
      return Promise.reject(error);
    }
  );

// Axios global (llamadas `axios.get(`${baseurlwc}/...`)` en los componentes)
axios.interceptors.request.use(withFirebaseToken(true));
releaseOnResponse(axios);

const api = create({
  baseURL: baseurlwc,
  headers: { Accept: "application/vnd.github.v3+json" },
});
api.axiosInstance.interceptors.request.use(withFirebaseToken(false));
releaseOnResponse(api.axiosInstance);

// --- Caché de catálogos (atributos, términos, categorías, marcas) -----------
// Memoria + sessionStorage, TTL 30 min. Evita repetir peticiones al navegar.
const CATALOG_TTL_MS = 30 * 60 * 1000;
const CACHE_PREFIX = "wc-cache:";
const memoryCache = new Map();
const inFlight = new Map();

const readCache = (key) => {
  const mem = memoryCache.get(key);
  if (mem && mem.expires > Date.now()) return mem.value;
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + key);
    if (raw) {
      const entry = JSON.parse(raw);
      if (entry.expires > Date.now()) {
        memoryCache.set(key, entry);
        return entry.value;
      }
    }
  } catch (e) {
    /* sessionStorage no disponible */
  }
  return null;
};

const writeCache = (key, value) => {
  const entry = { value, expires: Date.now() + CATALOG_TTL_MS };
  memoryCache.set(key, entry);
  try {
    sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
  } catch (e) {
    /* cuota llena o no disponible */
  }
};

export const clearCatalogCache = () => {
  memoryCache.clear();
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(CACHE_PREFIX))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch (e) {
    /* noop */
  }
};

// Solo cachea respuestas exitosas con arreglo; comparte peticiones en vuelo.
const cachedCatalog = (key, fetcher, force = false) => {
  if (!force) {
    const hit = readCache(key);
    if (hit) return Promise.resolve(hit);
    if (inFlight.has(key)) return inFlight.get(key);
  }
  const promise = fetcher()
    .then((response) => {
      if (response && response.ok && Array.isArray(response.data)) {
        writeCache(key, {
          ok: true,
          status: response.status,
          data: response.data,
        });
      }
      return response;
    })
    .finally(() => inFlight.delete(key));
  inFlight.set(key, promise);
  return promise;
};

// `force` evita la caché del navegador y la del proxy (nocache=1).
const nc = (force) => (force ? "&nocache=1" : "");

export const getOrderById = (orderId) =>
  api.get(`/orders/${orderId}?${credentials}`);


export const getCustomerOrders = () =>
  api.get(`/orders?status=enviado&${credentials}`);

export const getAllOrdersWhosales = (numpage = 1) =>
  api.get(
    `/orders?status=sendwholesaler&per_page=100&page=${numpage}&before=${NextMonth}&after=${ActualMonth}&${credentials}`
  );

export const getReportsTotal = () =>
  api.get(`/reports/orders/totals?${credentials}`);

export const reportSalesToday = () => api.get(`/reports/sales?${credentials}`);

export const getCustomerOrdersEnviados = () =>
  api.get(`/orders?status=processing&${credentials}`);

export const getCustomerOrderssendwholesaler = () =>
  api.get(`/orders?status=sendwholesaler&per_page=100&${credentials}`);


export const GetAllCategorys = (force = false) =>
  cachedCatalog(
    "categories",
    () => api.get(`/products/categories?${credentials}&per_page=100${nc(force)}`),
    force
  );

export const GetAllBrands = (force = false) =>
  cachedCatalog(
    "brands",
    () => api.get(`/products/brands?${credentials}&per_page=100${nc(force)}`),
    force
  );

export const GetAllBrandsPaginated = async (force = false) => {
  const cacheKey = "brands-paginated";
  if (!force) {
    const hit = readCache(cacheKey);
    if (hit) return hit;
  }
  const perPage = 100;
  let page = 1;
  let all = [];
  while (true) {
    const response = await api.get(
      `/products/brands?${credentials}&per_page=${perPage}&page=${page}&orderby=name&order=asc${nc(force)}`
    );
    if (!response.ok || !Array.isArray(response.data)) {
      throw new Error(response.data?.message || response.problem);
    }
    const brands = response.data;
    all = [...all, ...brands];
    if (brands.length < perPage) break;
    page++;
  }
  writeCache(cacheKey, all);
  return all;
};

export const AddBrand = async (data) => {
  const res = await api.post(`/products/brands?${credentials}`, data);
  clearCatalogCache();
  return res;
};

export const AddProductWC =(data) => api.post(`/products?${credentials}`, { data });

export const GetAllAttributes = (force = false) =>
  cachedCatalog(
    "attributes",
    () => api.get(`/products/attributes?${credentials}&per_page=100${nc(force)}`),
    force
  );

export const GetAllTermsAttributes = async (id, force = false) => {
  const cacheKey = `terms:${id}`;
  if (!force) {
    const hit = readCache(cacheKey);
    if (hit) return hit;
  }
  const perPage = 100;
  let page = 1;
  let allTerms = [];

  try {
    while (true) {
      const response = await api.get(
        `/products/attributes/${id}/terms?${credentials}&per_page=${perPage}&page=${page}${nc(force)}`
      );
      if (!Array.isArray(response.data)) {
        throw new Error(response.data?.message || response.problem || "respuesta inválida");
      }
      const terms = response.data;
      allTerms = [...allTerms, ...terms];
      if (terms.length < perPage) break;
      page++;
    }
    const result = { data: allTerms };
    writeCache(cacheKey, result);
    return result;
  } catch (error) {
    console.error(`Error fetching terms for attribute ${id}:`, error);
    throw new Error(`Failed to fetch terms for attribute ${id}: ${error.message}`);
  }
};

export const AddAttributes = (data) =>
  api.put(`/products/${Cookies.get('productid')}/?${credentials}`, data);

export const SearchVariableProducts = (search, perPage = 20) =>
  api.get(
    `/products?search=${encodeURIComponent(search)}&type=variable&per_page=${perPage}&${credentials}`
  );

export const GetProductById = (id) =>
  api.get(`/products/${id}?${credentials}`);